import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Search, FilePlus2, Download, FileText, Wand2 } from "lucide-react";
import { Loader } from "@/components/Loader";
import { RegionBadge, REGION_KEYS } from "@/components/RegionBadge";
import { FitBadge, FitLegend } from "@/components/FitBadge";
import { firmsToCSV, firmsToMarkdown, synergiesToCSV, downloadBlob } from "@/lib/export";

const fmtMoney = (n) => {
    if (!n) return "$0";
    if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
    if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
    if (n >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
    return `$${n.toFixed(0)}`;
};

export default function Library() {
    const [searchParams] = useSearchParams();
    const [firms, setFirms] = useState(null);
    const [synergies, setSynergies] = useState([]);
    const [q, setQ] = useState("");
    const [industry, setIndustry] = useState(searchParams.get("industry") || "All");
    const [region, setRegion] = useState(searchParams.get("region") || "All");
    const [bulkTagging, setBulkTagging] = useState(false);
    const [bulkMsg, setBulkMsg] = useState("");

    const loadFirms = () => api.get("/firms").then(({ data }) => setFirms(data));
    useEffect(() => {
        loadFirms();
        api.get("/synergies").then(({ data }) => setSynergies(data || [])).catch(() => {});
    }, []);

    // Sync URL params when they change (e.g. clicking a different pie slice while on Library)
    useEffect(() => {
        const r = searchParams.get("region");
        const i = searchParams.get("industry");
        if (r) setRegion(r);
        if (i) setIndustry(i);
    }, [searchParams]);

    const bulkAutoRegion = async () => {
        setBulkTagging(true); setBulkMsg("");
        try {
            const { data } = await api.post("/firms/auto-region/bulk");
            setBulkMsg(`Tagged ${data.updated} firm${data.updated === 1 ? "" : "s"}${data.skipped ? ` · ${data.skipped} already tagged` : ""}`);
            await loadFirms();
        } catch (e) {
            const d = e?.response?.data?.detail;
            setBulkMsg(typeof d === "string" ? d : "Bulk tagging failed");
        } finally {
            setBulkTagging(false);
            setTimeout(() => setBulkMsg(""), 5000);
        }
    };

    const industries = useMemo(() => {
        const set = new Set(["All"]);
        (firms || []).forEach((f) => set.add(f.industry || "Uncategorized"));
        return [...set];
    }, [firms]);

    const regions = useMemo(() => {
        const set = new Set(["All"]);
        (firms || []).forEach((f) => set.add(f.region || "Other"));
        return ["All", ...REGION_KEYS.filter((k) => set.has(k))];
    }, [firms]);

    const filtered = useMemo(() => {
        const list = firms || [];
        return list.filter((f) => {
            const matchesQ = !q || f.name.toLowerCase().includes(q.toLowerCase()) || (f.tags || []).some((t) => t.toLowerCase().includes(q.toLowerCase()));
            const matchesI = industry === "All" || (f.industry || "Uncategorized") === industry;
            const matchesR = region === "All" || (f.region || "Other") === region;
            return matchesQ && matchesI && matchesR;
        });
    }, [firms, q, industry, region]);

    if (firms === null) return <div className="p-12"><Loader label="Loading library" /></div>;

    return (
        <div className="p-8" data-testid="library-page">
            <header className="mb-8 flex items-end justify-between border-b border-zinc-800 pb-6 gap-4 flex-wrap">
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-2">// Library</div>
                    <h1 className="font-heading font-black tracking-tighter text-4xl text-zinc-50">My Research</h1>
                    <p className="text-sm text-zinc-500 mt-2">{firms.length} firm{firms.length === 1 ? "" : "s"} on file.</p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    <FitLegend />
                    <button
                        onClick={bulkAutoRegion}
                        disabled={bulkTagging || !firms.length}
                        data-testid="bulk-auto-region-btn"
                        className="inline-flex items-center gap-1.5 border border-zinc-800 hover:border-amber-500 hover:text-amber-500 text-xs font-mono uppercase tracking-wider text-zinc-400 px-2.5 py-2 transition-colors disabled:opacity-40"
                        title="Auto-tag region for every firm missing one (single AI call, cheap)"
                    >
                        <Wand2 className="w-3 h-3" /> {bulkTagging ? "Tagging…" : "Auto-tag regions"}
                    </button>
                    {bulkMsg && (
                        <span data-testid="bulk-msg" className="font-mono text-[10px] text-amber-400 uppercase tracking-wider">{bulkMsg}</span>
                    )}
                    <div className="flex items-center gap-1.5" data-testid="export-group">
                        <button
                            onClick={() => downloadBlob(firmsToCSV(filtered), `affynix-library-${new Date().toISOString().slice(0,10)}.csv`, "text/csv;charset=utf-8")}
                            disabled={!filtered.length}
                            data-testid="export-firms-csv"
                            className="inline-flex items-center gap-1.5 border border-zinc-800 hover:border-amber-500 hover:text-amber-500 text-xs font-mono uppercase tracking-wider text-zinc-400 px-2.5 py-2 transition-colors disabled:opacity-40"
                        >
                            <Download className="w-3 h-3" /> CSV
                        </button>
                        <button
                            onClick={() => downloadBlob(firmsToMarkdown(filtered), `affynix-library-${new Date().toISOString().slice(0,10)}.md`, "text/markdown;charset=utf-8")}
                            disabled={!filtered.length}
                            data-testid="export-firms-md"
                            className="inline-flex items-center gap-1.5 border border-zinc-800 hover:border-amber-500 hover:text-amber-500 text-xs font-mono uppercase tracking-wider text-zinc-400 px-2.5 py-2 transition-colors disabled:opacity-40"
                        >
                            <FileText className="w-3 h-3" /> MD
                        </button>
                        <button
                            onClick={() => downloadBlob(synergiesToCSV(synergies), `affynix-synergies-${new Date().toISOString().slice(0,10)}.csv`, "text/csv;charset=utf-8")}
                            disabled={!synergies.length}
                            data-testid="export-synergies-csv"
                            className="inline-flex items-center gap-1.5 border border-zinc-800 hover:border-amber-500 hover:text-amber-500 text-xs font-mono uppercase tracking-wider text-zinc-400 px-2.5 py-2 transition-colors disabled:opacity-40"
                            title="Export saved synergies as CSV"
                        >
                            <Download className="w-3 h-3" /> Synergies
                        </button>
                    </div>
                    <Link to="/app/research" data-testid="library-new-research-btn" className="bg-amber-500 hover:bg-amber-400 text-black font-semibold text-xs uppercase tracking-wider px-4 py-2.5 flex items-center gap-2">
                        <FilePlus2 className="w-3.5 h-3.5" /> New research
                    </Link>
                </div>
            </header>

            <div className="mb-6 flex items-center gap-3 flex-wrap">
                <div className="flex items-center border border-zinc-800 focus-within:border-amber-500 bg-zinc-950 flex-1 min-w-[260px]">
                    <Search className="w-4 h-4 ml-3 text-zinc-500" />
                    <input
                        data-testid="library-search-input"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Filter firms or tags..."
                        className="flex-1 bg-transparent px-3 py-2.5 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none font-mono"
                    />
                </div>
                <div className="flex items-center gap-1.5 overflow-x-auto">
                    {regions.map((r) => (
                        <button
                            key={r}
                            data-testid={`region-filter-${r}`}
                            onClick={() => setRegion(r)}
                            className={`text-xs px-2.5 py-1.5 border whitespace-nowrap transition-colors ${
                                region === r
                                    ? "border-amber-500 bg-amber-500/10 text-amber-400"
                                    : "border-zinc-800 text-zinc-400 hover:border-zinc-600"
                            }`}
                        >
                            {r}
                        </button>
                    ))}
                </div>
                <div className="flex items-center gap-1.5 overflow-x-auto">
                    {industries.map((i) => (
                        <button
                            key={i}
                            data-testid={`industry-filter-${i}`}
                            onClick={() => setIndustry(i)}
                            className={`text-xs px-2.5 py-1.5 border whitespace-nowrap transition-colors ${
                                industry === i
                                    ? "border-amber-500 bg-amber-500/10 text-amber-400"
                                    : "border-zinc-800 text-zinc-400 hover:border-zinc-600"
                            }`}
                        >
                            {i}
                        </button>
                    ))}
                </div>
            </div>

            {filtered.length === 0 ? (
                <div className="border border-dashed border-zinc-800 p-16 text-center">
                    <div className="font-heading font-bold text-zinc-300 text-xl mb-2">No firms yet</div>
                    <div className="text-sm text-zinc-500 mb-5">Start by researching your first company.</div>
                    <Link to="/app/research" className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black font-semibold text-xs uppercase tracking-wider px-4 py-2.5">
                        Run research
                    </Link>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {filtered.map((f) => {
                        const revenue = (f.products || []).reduce((s, p) => s + (Number(p.annual_revenue_usd) || 0), 0);
                        return (
                            <Link key={f.id} to={`/app/firms/${f.id}`} data-testid={`firm-card-${f.id}`}
                                style={f.color ? { borderLeftColor: f.color, borderLeftWidth: "3px" } : undefined}
                                className="border border-zinc-800 bg-zinc-950 p-5 hover:border-amber-500/60 hover:bg-zinc-900/50 transition-colors group">
                                <div className="flex items-start justify-between mb-3 gap-2">
                                    <div className="flex items-center gap-2 min-w-0">
                                        {f.color && <span className="w-2.5 h-2.5 rounded-full inline-block shrink-0" style={{ background: f.color }} />}
                                        <div className="font-heading font-bold text-lg text-zinc-50 group-hover:text-amber-400 transition-colors truncate">{f.name}</div>
                                        <FitBadge firmId={f.id} synergies={synergies} allFirms={firms} />
                                    </div>
                                    <RegionBadge region={f.region} />
                                </div>
                                <div className="text-xs font-mono text-zinc-500 mb-3">{f.industry || "—"} · {f.headquarters || "—"}</div>
                                <p className="text-xs text-zinc-400 line-clamp-2 mb-4 leading-relaxed">{f.overview || "No overview yet."}</p>
                                <div className="flex items-center justify-between border-t border-zinc-800 pt-3">
                                    <div className="font-mono text-xs text-amber-500">{fmtMoney(revenue)}</div>
                                    <div className="flex gap-1">
                                        {(f.tags || []).slice(0, 2).map((t) => (
                                            <span key={t} className="text-[10px] border border-zinc-700 px-1.5 py-0.5 text-zinc-400">{t}</span>
                                        ))}
                                    </div>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
